import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import "../styles/PaymentAuthorization.css";
import "../styles/Questionnaire.css";
import LockSVG from "assets/svg/LockSVG";
import {
  createCardSetupIntentApi,
  completeCardSetupApi,
} from "services/postApis";
import { updateConsultationStatusApi } from "services/patchApis";
import { getSavedCardsApi } from "services/getApis";
import { ErrorHandler } from "utils/toast";
import { useRedirectTo } from "hooks/useRedirectTo";
import ButtonLoader from "components/ButtonLoader";
import ConfirmationModal from "components/ConfirmationModal";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const getCardId = (c) => c?.id || c?.paymentMethodId || c?.payment_method_id || c?._id;
const getCardBrand = (c) => c?.brand || c?.card?.brand || "Card";
const getCardLast4 = (c) => c?.last4 || c?.card?.last4 || "****";
const getCardExp = (c) => {
  const month = c?.expMonth || c?.exp_month || c?.card?.exp_month || c?.card?.expMonth;
  const year = c?.expYear || c?.exp_year || c?.card?.exp_year || c?.card?.expYear;
  if (month && year) {
    return `${String(month).padStart(2, "0")}/${String(year).slice(-2)}`;
  }
  return null;
};

// ── Local record of a completed card authorization, keyed by submission ──
// Avoids re-prompting for card details if the user revisits this page after
// already successfully authorizing a card for this questionnaire submission.
const authKey = (userSubmissionId) => `pa-card-authorized-${userSubmissionId}`;

const getStoredAuthorization = (userSubmissionId) => {
  if (!userSubmissionId) return null;
  try {
    const raw = localStorage.getItem(authKey(userSubmissionId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const storeAuthorization = (userSubmissionId, data) => {
  if (!userSubmissionId) return;
  try {
    localStorage.setItem(authKey(userSubmissionId), JSON.stringify(data));
  } catch {
    // Storage unavailable — not fatal, worst case the user is asked again.
  }
};

const clearStoredAuthorization = (userSubmissionId) => {
  if (!userSubmissionId) return;
  try {
    localStorage.removeItem(authKey(userSubmissionId));
  } catch {
    // ignore
  }
};

// ── Static billing breakdown based on path ──────────────────
const BILLING_BREAKDOWN = {
  pathA: [
    { label: "Practitioner intake review", value: "Included" },
    { label: "Personalised herbal recommendation", value: "Included" },
    { label: "If you approve — formula + shipping", value: "$179 total" },
    { label: "If you decline — consultation fee only", value: "$99" },
    { label: "Charged now", value: "$0", variant: "green" },
  ],
  pathB: [
    { label: "Live Zoom consultation", value: "Included" },
    { label: "Practitioner intake review", value: "Included" },
    { label: "Personalised herbal recommendation", value: "Included" },
    { label: "Herbal formula preparation & shipping", value: "Included" },
    { label: "Total charged on finalization", value: "$275" },
    { label: "Charged now", value: "$0", variant: "green" },
  ],
};

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontFamily: '"DM Sans", "Inter", sans-serif',
      fontSize: "15px",
      color: "#111827",
      "::placeholder": { color: "#9ca3af" },
    },
    invalid: { color: "#dc2626" },
  },
};

// ── Card form + SetupIntent confirmation (needs Stripe Elements context) ──
const PaymentAuthorizationForm = ({
  userSubmissionId,
  selectedPath,
  pathLabel,
  pathTitle,
  price,
  priceNote,
  initialSavedCards = [],
}) => {
  const navigate = useNavigate();
  const { redirectTo } = useRedirectTo();
  const stripe = useStripe();
  const elements = useElements();

  const billing = BILLING_BREAKDOWN[selectedPath] || BILLING_BREAKDOWN.pathA;
  const consultationType =
    selectedPath === "pathB"
      ? "zoom_consultation"
      : "custom_herbal_recommendation";

  const [cardName, setCardName] = useState("");
  const [cardComplete, setCardComplete] = useState(false);
  const [cardError, setCardError] = useState("");
  const [cardFocused, setCardFocused] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedCardOption, setSelectedCardOption] = useState("new");

  // States for complete card setup API & retry modal
  const [pendingSetupIntentId, setPendingSetupIntentId] = useState(null);
  const [completedPaymentMethodId, setCompletedPaymentMethodId] = useState(null);
  const [showCompleteErrorModal, setShowCompleteErrorModal] = useState(false);
  const [isRetryingComplete, setIsRetryingComplete] = useState(false);

  // Fetch saved cards query
  const { data: savedCardsRes } = useQuery({
    queryKey: ["savedCards"],
    queryFn: getSavedCardsApi,
    refetchOnWindowFocus: false,
  });

  const savedCards = useMemo(() => {
    const fetched = savedCardsRes?.payload || savedCardsRes?.data || savedCardsRes;
    if (Array.isArray(fetched) && fetched.length > 0) return fetched;
    if (Array.isArray(initialSavedCards) && initialSavedCards.length > 0) return initialSavedCards;
    return [];
  }, [savedCardsRes, initialSavedCards]);

  // Default to first saved card if available
  useEffect(() => {
    if (savedCards.length > 0 && selectedCardOption === "new") {
      const firstId = getCardId(savedCards[0]);
      if (firstId) {
        setSelectedCardOption(firstId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedCards]);

  // If this submission already has a card on file (from a previous visit to
  // this page), skip straight to the "already authorized" view instead of
  // creating a new SetupIntent and asking for card details again.
  const [existingAuth, setExistingAuth] = useState(() =>
    getStoredAuthorization(userSubmissionId),
  );

  const isFormValid =
    selectedCardOption !== "new"
      ? true
      : cardName.trim().length > 1 && cardComplete;

  // Helper to call backend payments/card/setup-complete API
  const handleCompleteSetup = async (setupIntentId, paymentMethodId) => {
    try {
      setIsRetryingComplete(true);
      await completeCardSetupApi({ setupIntentId });

      // API call succeeded!
      setShowCompleteErrorModal(false);
      storeAuthorization(userSubmissionId, {
        setupIntentId,
        paymentMethodId,
        selectedPath,
        pathLabel,
        pathTitle,
        price,
      });
      setPendingSetupIntentId(setupIntentId);
      setCompletedPaymentMethodId(paymentMethodId);
      // Inform backend by updating consultation status in background
      if (userSubmissionId) {
        updateConsultationStatusApi({
          userSubmissionId,
          consultationType,
        }).catch((err) => {
          console.error("Background consultation status update failed:", err);
        });
      }

      if (selectedPath === "pathB") {
        navigate("/schedule", {
          state: { userSubmissionId },
          replace: true,
        });
      } else {
        setShowSuccess(true);
      }
    } catch (error) {
      console.error("Card setup complete API error:", error);
      setPendingSetupIntentId(setupIntentId);
      setCompletedPaymentMethodId(paymentMethodId);
      setShowCompleteErrorModal(true);
    } finally {
      setIsRetryingComplete(false);
      setConfirming(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid || confirming) return;

    setConfirming(true);
    setCardError("");

    // Flow 1: User selected a saved card
    if (selectedCardOption !== "new") {
      const selectedCard = savedCards.find((c) => getCardId(c) === selectedCardOption);
      storeAuthorization(userSubmissionId, {
        setupIntentId: null,
        paymentMethodId: selectedCardOption,
        cardBrand: getCardBrand(selectedCard),
        last4: getCardLast4(selectedCard),
        selectedPath,
        pathLabel,
        pathTitle,
        price,
      });
      setCompletedPaymentMethodId(selectedCardOption);

      // Call update-consultation-status API in background with paymentMethodId
      if (userSubmissionId) {
        updateConsultationStatusApi({
          userSubmissionId,
          consultationType,
          paymentMethodId: selectedCardOption,
        }).catch((err) => {
          console.error("Background consultation status update failed:", err);
        });
      }

      setConfirming(false);

      if (selectedPath === "pathB") {
        navigate("/schedule", {
          state: { userSubmissionId },
          replace: true,
        });
      } else {
        setShowSuccess(true);
      }
      return;
    }

    // Flow 2: User authorized with a new card via Stripe
    if (!stripe || !elements) return;

    try {
      const res = await createCardSetupIntentApi({ userSubmissionId });
      const clientSecret = res?.payload?.clientSecret || res?.clientSecret;

      if (!clientSecret) {
        setCardError("Unable to prepare card authorization. Please try again.");
        setConfirming(false);
        return;
      }

      const { error, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: { name: cardName },
          },
        },
      );

      if (error) {
        setCardError(
          error.message || "We couldn't authorize your card. Please try again.",
        );
        setConfirming(false);
        return;
      }

      if (setupIntent?.status === "succeeded") {
        await handleCompleteSetup(setupIntent.id, setupIntent.payment_method);
      } else {
        setCardError("Card authorization is still pending. Please try again.");
        setConfirming(false);
      }
    } catch (err) {
      console.error("Setup intent creation error:", err);
      ErrorHandler(err);
      setCardError(
        err?.response?.data?.message || err?.message || "Failed to process card authorization. Please try again."
      );
      setConfirming(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────
  if (showSuccess) {
    return (
      <div className="questionnaire-container">
        <div className="questionnaire-content">
          <div className="pa-success-wrap">
            <div className="pa-success-card">
              <div className="pa-success-icon-circle">
                <svg className="pa-success-checkmark" viewBox="0 0 52 52">
                  <circle
                    className="pa-success-circle"
                    cx="26"
                    cy="26"
                    r="25"
                    fill="none"
                  />
                  <path
                    className="pa-success-check"
                    fill="none"
                    d="M14.1 27.2l7.1 7.2 16.7-16.8"
                  />
                </svg>
              </div>
              <h2 className="pa-success-title">Card Authorized</h2>
              <p className="pa-success-desc">
                Your payment method has been securely saved.{" "}
                <strong>No charge has been made.</strong> Your practitioner will
                review your intake and you'll be notified once your
                recommendation is ready.
              </p>
              <p className="pa-success-path">
                {pathLabel} — {pathTitle}
              </p>
              <button
                id="pa-success-home-btn"
                type="button"
                className="approval-btn primary"
                style={{ width: "100%", marginTop: 24 }}
                onClick={() => redirectTo()}
              >
                Return to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Already authorized on a previous visit ──────────────────
  if (existingAuth) {
    return (
      <div className="questionnaire-container">
        <div className="questionnaire-content">
          <div className="pa-success-wrap">
            <div className="pa-success-card">
              <div className="pa-success-icon-circle">
                <svg className="pa-success-checkmark" viewBox="0 0 52 52">
                  <circle
                    className="pa-success-circle"
                    cx="26"
                    cy="26"
                    r="25"
                    fill="none"
                  />
                  <path
                    className="pa-success-check"
                    fill="none"
                    d="M14.1 27.2l7.1 7.2 16.7-16.8"
                  />
                </svg>
              </div>
              <h2 className="pa-success-title">Card Already Authorized</h2>
              <p className="pa-success-desc">
                You've already saved a payment method for this submission.{" "}
                <strong>No charge has been made.</strong> Your practitioner
                will review your intake and you'll be notified once your
                recommendation is ready.
              </p>
              <p className="pa-success-path">
                {existingAuth.pathLabel || pathLabel} —{" "}
                {existingAuth.pathTitle || pathTitle}
              </p>

              <button
                id="pa-continue-btn"
                type="button"
                className="approval-btn primary"
                style={{ width: "100%", marginTop: 24 }}
                onClick={() => {
                  const effectivePath = existingAuth.selectedPath || selectedPath;
                  if (effectivePath === "pathB") {
                    navigate("/schedule", {
                      state: { userSubmissionId },
                      replace: true,
                    });
                  } else {
                    redirectTo();
                  }
                }}
              >
                Continue
              </button>

              {/* <button
                type="button"
                className="pa-link-btn"
                onClick={() => {
                  clearStoredAuthorization(userSubmissionId);
                  setExistingAuth(null);
                }}
              >
                Use a different card instead
              </button> */}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="questionnaire-container">
      <div className="questionnaire-content">
        <div style={{ marginTop: "8%" }}>
          {/* Header */}
          <div className="co-header">
            <h1 className="co-heading">Authorize your payment method.</h1>
            <p className="co-subheading">
              We collect your card details now so we can process payment after
              the practitioner finalises your recommendation. You will{" "}
              <strong>not be charged today</strong>.
            </p>
          </div>

          {/* Selected path summary */}
          <div className="pa-summary-card">
            <div className="pa-summary-left">
              <p className="pa-summary-path-label">{pathLabel} — Selected</p>
              <p className="pa-summary-title">{pathTitle}</p>
              <p className="pa-summary-note">{priceNote}</p>
            </div>
            <p className="pa-summary-price">{price}</p>
          </div>

          {/* No-charge notice */}
          <div className="pa-no-charge-banner">
            <span className="pa-no-charge-icon" aria-hidden="true">
              <LockSVG size={18} color="#16a34a" />
            </span>
            <p className="pa-no-charge-text">
              <strong>Your card will not be charged right now.</strong> Payment
              is only processed after your practitioner reviews your intake and
              the recommendation is finalised. You'll have a chance to review
              before any charge is made.
            </p>
          </div>

          {/* Card form */}
          <form className="pa-form-card" onSubmit={handleSubmit}>
            <p className="pa-form-section-title">Payment Method</p>

            {/* Saved cards list */}
            {savedCards.length > 0 && (
              <div className="pa-saved-cards-section">
                <p className="pa-saved-cards-subtitle">Your saved cards</p>
                <div className="pa-saved-cards-list">
                  {savedCards.map((card, idx) => {
                    const cardId = getCardId(card) || `card-${idx}`;
                    const isSelected = selectedCardOption === cardId;
                    const brand = getCardBrand(card);
                    const last4 = getCardLast4(card);
                    const exp = getCardExp(card);

                    return (
                      <div
                        key={cardId}
                        className={`pa-saved-card-item ${isSelected ? "pa-saved-card-item--selected" : ""
                          }`}
                        onClick={() => setSelectedCardOption(cardId)}
                        role="radio"
                        aria-checked={isSelected}
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            setSelectedCardOption(cardId);
                          }
                        }}
                      >
                        <div className="pa-saved-card-radio">
                          <div className="pa-saved-card-dot" />
                        </div>
                        <div className="pa-saved-card-brand-tag">
                          {brand.toUpperCase()}
                        </div>
                        <div className="pa-saved-card-details">
                          <span className="pa-saved-card-digits">
                            •••• •••• •••• {last4}
                          </span>
                          {exp && (
                            <span className="pa-saved-card-exp">Exp {exp}</span>
                          )}
                        </div>
                      </div>
                    );
                  })}

                  {/* Option to add new card */}
                  <div
                    className={`pa-saved-card-item ${selectedCardOption === "new" ? "pa-saved-card-item--selected" : ""
                      }`}
                    onClick={() => setSelectedCardOption("new")}
                    role="radio"
                    aria-checked={selectedCardOption === "new"}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        setSelectedCardOption("new");
                      }
                    }}
                  >
                    <div className="pa-saved-card-radio">
                      <div className="pa-saved-card-dot" />
                    </div>
                    <div className="pa-saved-card-details">
                      <span className="pa-saved-card-add-new">+ Use a new card</span>
                    </div>
                  </div>
                </div>

                {selectedCardOption !== "new" && <div className="pa-form-divider" />}
              </div>
            )}

            {/* New card details input form */}
            {(savedCards.length === 0 || selectedCardOption === "new") && (
              <div className="pa-new-card-fields">
                {savedCards.length > 0 && (
                  <p className="pa-saved-cards-subtitle" style={{ marginTop: 12 }}>
                    Enter new card details
                  </p>
                )}

                {/* Cardholder name */}
                <div className="pa-form-group">
                  <label className="pa-form-label" htmlFor="pa-card-name">
                    Cardholder name
                  </label>
                  <input
                    id="pa-card-name"
                    type="text"
                    className="pa-form-input"
                    placeholder="Jane Smith"
                    value={cardName}
                    onChange={(e) => setCardName(e.target.value)}
                  />
                </div>

                {/* Stripe's built-in, PCI-compliant card element */}
                <div className="pa-form-group">
                  <label className="pa-form-label" htmlFor="pa-card-element">
                    Card details
                  </label>
                  <div
                    className={`pa-form-input pa-stripe-card-wrap${cardFocused ? " pa-stripe-card-wrap--focus" : ""
                      }${cardError ? " pa-stripe-card-wrap--invalid" : ""}`}
                  >
                    <CardElement
                      id="pa-card-element"
                      options={CARD_ELEMENT_OPTIONS}
                      onFocus={() => setCardFocused(true)}
                      onBlur={() => setCardFocused(false)}
                      onChange={(e) => {
                        setCardComplete(e.complete);
                        setCardError(e.error ? e.error.message : "");
                      }}
                    />
                  </div>
                  {cardError && <p className="pa-card-error">{cardError}</p>}
                </div>

                <div className="pa-form-divider" />
              </div>
            )}

            {/* Submit button */}
            <button
              id="pa-submit-btn"
              type="submit"
              className="approval-btn primary"
              style={{ width: "100%" }}
              disabled={!isFormValid || confirming}
            >
              {confirming ? (
                <ButtonLoader />
              ) : (
                `Authorize card — charged ${price} after review`
              )}
            </button>

            <p className="pa-bottom-note">
              You will receive an email once your practitioner has reviewed your
              intake. No amount will be deducted until that step is complete.
            </p>
          </form>

          {/* Billing breakdown */}
          <div className="pa-billing-card">
            <p className="pa-billing-title">What you're paying for</p>
            {billing.map((row, i) => (
              <React.Fragment key={i}>
                {row.divider ? (
                  <div className="pa-billing-divider" />
                ) : (
                  <div className="pa-billing-row">
                    <span className="pa-billing-label">{row.label}</span>
                    <span
                      className={`pa-billing-value${row.variant === "green"
                        ? " pa-billing-value--green"
                        : row.variant === "muted"
                          ? " pa-billing-value--muted"
                          : ""
                        }`}
                    >
                      {row.value}
                    </span>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          <div style={{ paddingBottom: "60px" }} />
        </div>
      </div>

      <ConfirmationModal
        show={showCompleteErrorModal}
        onCancel={() => { }}
        onConfirm={() => {
          if (pendingSetupIntentId) {
            handleCompleteSetup(pendingSetupIntentId, completedPaymentMethodId);
          }
        }}
        title="Submission Error"
        description="There might be an error while submitting your card. Please try again."
        confirmText="Try Again"
        hideCancel={true}
        isLoading={isRetryingComplete}
      />
    </div>
  );
};

const PaymentAuthorization = () => {
  const location = useLocation();

  // Data passed from ConsultationOptions
  const {
    userSubmissionId,
    selectedPath = "pathA",
    pathLabel = "Path A",
    pathTitle = "Custom Herbal Recommendation",
    price = "$179",
    priceNote = "$99 consultation fee if you decline the formula",
    initialSavedCards = [],
  } = location.state || {};

  return (
    <Elements stripe={stripePromise}>
      <PaymentAuthorizationForm
        userSubmissionId={userSubmissionId}
        selectedPath={selectedPath}
        pathLabel={pathLabel}
        pathTitle={pathTitle}
        price={price}
        priceNote={priceNote}
        initialSavedCards={initialSavedCards}
      />
    </Elements>
  );
};

export default PaymentAuthorization;
